import { useCallback, useEffect, useState } from 'react'

import {
  deleteKpiUserAssignment,
  getKpiAssignmentMetaData,
  getKpiUserAssignments,
  postKpiUserAssignments,
} from '@/api/kpi'
import {
  KpiAssignmentMetaDataModel,
  KpiAssignmentMetaDataResponseModel,
  KpiUserAssignmentBodyModel,
  KpiUserAssignmentCreationModel,
  KpiUserAssignmentsModel,
} from '@/types'

interface FetchUserAssignmentsParams {
  branchName?: string
  year?: number
  quarter?: number
  userId?: string
}

interface UseKpiUserGoalAssignmentsReturn {
  metadata: KpiAssignmentMetaDataModel | null
  userAssignments: KpiUserAssignmentsModel[] | null
  loading: boolean
  error: Error | null
  fetchMetadata: (branchName: string, includeUsers?: boolean) => Promise<void>
  fetchUserAssignments: (params?: FetchUserAssignmentsParams) => Promise<void>
  createUserAssignments: (
    assignments: KpiUserAssignmentCreationModel[]
  ) => Promise<void>
  deleteUserAssignment: (id: string) => Promise<void>
}

const KPI_CATEGORY_MAPPING: Record<string, string> = {
  sales: 'Sales',
  labor: 'Labor',
  speed_of_service: 'Speed Of Service',
  loyalty: 'Loyalty',
  spend: 'Spend',
} as const

const useKpiUserGoalAssignments = (
  initialBranchName?: string
): UseKpiUserGoalAssignmentsReturn => {
  const [metadata, setMetadata] = useState<KpiAssignmentMetaDataModel | null>(
    null
  )
  const [userAssignments, setUserAssignments] = useState<
    KpiUserAssignmentsModel[] | null
  >(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchMetadata = useCallback(
    async (branchName: string, includeUsers: boolean = true) => {
      if (!branchName) {
        setMetadata(null)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const data: KpiAssignmentMetaDataResponseModel =
          await getKpiAssignmentMetaData(branchName, includeUsers)

        const transformedMetadata: KpiAssignmentMetaDataModel = {
          branch: data.branch,
          eligibleUsers: data.eligible_users?.map((user) => ({
            id: user.id,
            username: user.username,
            email: user.email,
            fullName: user.full_name,
          })),
          branchKpis: data.branch_kpis?.map((kpi) => ({
            id: kpi.id,
            kpiMetric: kpi.kpi_metric,
            kpiGroup: kpi.kpi_group,
            kpiUnit: kpi.kpi_unit,
            year: kpi.year,
            quarter: kpi.quarter,
            targetValue: kpi.target_value,
            bonusAmount: kpi.bonus_amount,
          })),
        }

        setMetadata(transformedMetadata)
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error('Failed to fetch KPI assignment metadata')
        setError(error)
        console.error('Error fetching KPI assignment metadata:', error)
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const fetchUserAssignments = useCallback(
    async (params?: FetchUserAssignmentsParams) => {
      setLoading(true)
      setError(null)

      try {
        const data = await getKpiUserAssignments(
          params?.branchName,
          params?.year,
          params?.quarter,
          params?.userId
        )

        const assignments: KpiUserAssignmentsModel[] = data.map(
          (assignment) => ({
            id: assignment.id,
            branchKpiAssignmentId: assignment.branch_kpi_assignment_id,
            userId: assignment.user_id,
            userUsername: assignment.user_username,
            userEmail: assignment.user_email,
            userFullName: assignment.user_full_name,
            kpiMetric: assignment.kpi_metric,
            branchId: assignment.branch_id,
            branchName: assignment.branch_name,
            year: assignment.year,
            quarter: assignment.quarter,
            targetValue: assignment.target_value,
            bonusAmount: assignment.bonus_amount,
            actualValue: assignment.actual_value,
            achieved: assignment.achieved,
            progressPercent: assignment.progress_percent,
            createdAt: assignment.created_at,
            kpiName: assignment.kpi_name,
            kpiUnit: assignment.kpi_unit,
            kpiGroup:
              KPI_CATEGORY_MAPPING[assignment.kpi_group] ||
              assignment.kpi_group,
            granularity: assignment.granularity,
            lowerIsBetter: assignment.lower_is_better,
          })
        )

        setUserAssignments(assignments)
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error('Failed to fetch KPI user assignments')
        setError(error)
        console.error('Error fetching KPI user assignments:', error)
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const createUserAssignments = useCallback(
    async (newAssignments: KpiUserAssignmentCreationModel[]) => {
      setLoading(true)
      setError(null)

      const assignments: KpiUserAssignmentBodyModel[] = newAssignments.map(
        (assignment) => ({
          branch_kpi_assignment: assignment.branchKpiAssignmentId,
          user: assignment.user,
          bonus_amount: assignment.bonusAmount,
        })
      )

      try {
        await postKpiUserAssignments(assignments)
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error('Failed to create KPI user assignments')
        setError(error)
        console.error('Error creating KPI user assignments:', error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const deleteUserAssignment = useCallback(async (id: string) => {
    setLoading(true)
    setError(null)

    try {
      await deleteKpiUserAssignment(id)
      setUserAssignments((prev) =>
        prev ? prev.filter((assignment) => assignment.id !== id) : prev
      )
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error('Failed to delete KPI user assignment')
      setError(error)
      console.error('Error deleting KPI user assignment:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (initialBranchName) {
      fetchMetadata(initialBranchName, true)
    }
  }, [])

  return {
    metadata,
    userAssignments,
    loading,
    error,
    fetchMetadata,
    fetchUserAssignments,
    createUserAssignments,
    deleteUserAssignment,
  }
}

export default useKpiUserGoalAssignments
